import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';
import ToolsSection from '../../components/feature/ToolsSection';

export default function ServiceDetailPage() {
  const { serviceName } = useParams();
  const [selectedProject, setSelectedProject] = useState<number | null>(null);

  // Service data mapping
  const serviceData: Record<string, any> = {
    'architectural-floor-plans': {
      title: 'Architectural Floor Plans',
      subtitle: 'Detailed layouts for residential and commercial building projects',
      heroImage: 'https://images.unsplash.com/photo-1503387762-592dea58ef21?q=80&w=2000&auto=format&fit=crop',
      description: {
        what: 'Our architectural floor plan services deliver detailed 2D layouts including room arrangements, precise dimensions, door/window schedules, and spatial organization. We ensure every square foot is optimized for its intended use.',
        who: 'Ideal for architects, developers, and homeowners who need accurate drawings for permitting, construction, or interior space planning.',
        why: 'Accurate floor plans are the blueprint for project success. They prevent spatial conflicts, ensure building code compliance, and provide a clear vision for all stakeholders involved in the build.'
      },
      valueProps: [
        { icon: 'ri-ruler-2-line', title: 'Metric Accuracy', description: 'Precision measurements for reliable on-site construction.' },
        { icon: 'ri-layout-grid-line', title: 'Flow Optimization', description: 'Expert spatial planning for maximum functional efficiency.' },
        { icon: 'ri-file-check-line', title: 'Code Compliant', description: 'All plans adhere to local building regulations and safety standards.' }
      ],
      portfolioProjects: [
        { title: 'Modern Villa', caption: 'Open-concept residential floor plan', image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=600' },
        { title: 'Tech Hub Office', caption: 'Corporate workspace with collaborative zones', image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=600' }
      ],
      caseStudy: {
        title: 'Centennial Office Park',
        description: 'Coordinated floor plans for a 50,000 sq ft commercial development, resulting in a 15% improvement in usable space through expert layout optimization.',
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=600'
      }
    },
    'structural-elevations': {
      title: 'Structural Elevations',
      subtitle: 'Precision vertical views showing building heights and structural assemblies',
      heroImage: 'https://images.unsplash.com/photo-1541888946425-d81bb19480c5?q=80&w=2000&auto=format&fit=crop',
      description: {
        what: 'We provide comprehensive structural elevations and section plans showing vertical relationships, floor levels, roof structures, and detailed wall assemblies with material callouts.',
        who: 'Structural engineers, contractors, and builders requiring clear documentation of a building\'s vertical structure and material specifications.',
        why: 'Clear vertical documentation is essential for structural integrity and accurate material procurement. Our elevations ensure that building heights and assemblies are executed perfectly on site.'
      },
      valueProps: [
        { icon: 'ri-building-2-line', title: 'Multi-Level Detail', description: 'Clear representation of floor heights and structural spans.' },
        { icon: 'ri-shield-check-line', title: 'Integrity Focused', description: 'Focus on load-bearing elements and assembly sequences.' },
        { icon: 'ri-tools-line', title: 'Material Specific', description: 'Detailed callouts for steel, concrete, and timber components.' }
      ],
      portfolioProjects: [
        { title: 'High-Rise Core', caption: 'Detailed core and shell elevations', image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=600' },
        { title: 'Bridging Structure', caption: 'Structural sections for pedestrian walkway', image: 'https://images.unsplash.com/photo-1545620001-f288414434ad?auto=format&fit=crop&q=80&w=600' }
      ],
      caseStudy: {
        title: 'Harbor Deck Project',
        description: 'Provided complex structural sections for a multi-level waterfront facility, ensuring perfect alignment between the foundation and pre-cast concrete elements.',
        image: 'https://images.unsplash.com/photo-1590487988256-9ed24133863e?auto=format&fit=crop&q=80&w=600'
      }
    },
    'rcp-plans': {
      title: 'RCP Plans',
      subtitle: 'Reflected Ceiling Plans for coordinated building systems',
      heroImage: 'https://images.unsplash.com/photo-1513506003901-1e6a229e2d15?q=80&w=2000&auto=format&fit=crop',
      description: {
        what: 'Our RCP services detail ceiling layouts, including lighting fixtures, HVAC diffusers, sprinkler heads, and ceiling heights, ensuring all overhead systems are perfectly coordinated.',
        who: 'MEP contractors, interior designers, and project managers coordinating the dense overhead infrastructure of modern buildings.',
        why: 'Coordinating ceiling systems is one of the biggest challenges in construction. Our RCP plans prevent clashes between trades and ensure the aesthetics of the ceiling match the lighting design.'
      },
      valueProps: [
        { icon: 'ri-lightbulb-line', title: 'System Coordination', description: 'Eliminating clashes between HVAC, Electrical, and Fire Fighting.' },
        { icon: 'ri-layout-3-line', title: 'Aesthetic Alignment', description: 'Ensuring fixture layouts match interior design intent.' },
        { icon: 'ri-ruler-line', title: 'Height Clarity', description: 'Precise marking of dropped ceilings and bulkhead details.' }
      ],
      portfolioProjects: [
        { title: 'Grand Lobby', caption: 'Intricate lighting and acoustic ceiling layout', image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=600' },
        { title: 'Data Center', caption: 'Coordinated cable tray and fire suppression RCP', image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80&w=600' }
      ],
      caseStudy: {
        title: 'Aurora Hotel Renovation',
        description: 'Coordinated the RCP for a luxury hotel renovation where ceiling space was extremely tight, reducing field clashes by 90%.',
        image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&q=80&w=600'
      }
    },
    '3d-photorealistic-renderings': {
      title: '3D Photorealistic Renderings',
      subtitle: 'Bringing visions to life with high-fidelity visualizations',
      heroImage: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=80&w=2000&auto=format&fit=crop',
      description: {
        what: 'We create stunning 3D visualizations for interior and exterior spaces. Using advanced lighting, material physics, and texturing, we produce images that are indistinguishable from photographs.',
        who: 'Developers looking for marketing material, architects presenting to clients, and homeowners wanting to visualize their future space.',
        why: 'Visualizing a space before it\'s built is a powerful sales and design tool. It helps secure approvals, sell properties faster, and prevents design disappointments.'
      },
      valueProps: [
        { icon: 'ri-image-2-line', title: 'High Fidelity', description: 'Photorealistic quality with advanced global illumination.' },
        { icon: 'ri-palette-line', title: 'Material Accuracy', description: 'Realistic representation of textures, finishes, and fabrics.' },
        { icon: 'ri-magic-line', title: 'Design Impact', description: 'Compositions that capture the mood and intent of the project.' }
      ],
      portfolioProjects: [
        { title: 'Oceanfront Villa', caption: 'Exterior dusk rendering', image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&q=80&w=600' },
        { title: 'Penthouse Living', caption: 'Modern minimalist interior rendering', image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80&w=600' }
      ],
      caseStudy: {
        title: 'Skyline Plaza Marketing',
        description: 'Our renderings helped a property developer secure 40% pre-sales before breaking ground on a residential tower.',
        image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&q=80&w=600'
      }
    },
    'line-diagrams': {
      title: 'Line Diagrams',
      subtitle: 'Technical schematics for complex building systems',
      heroImage: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2000&auto=format&fit=crop',
      description: {
        what: 'Our line diagram services cover single-line electrical schematics, plumbing flow charts, and system logic diagrams for building automation and coordination.',
        who: 'Electrical and mechanical contractors, system integrators, and facility managers.',
        why: 'Technical diagrams are essential for safety and maintenance. A clear line diagram ensures that systems are installed correctly and can be serviced easily throughout their lifecycle.'
      },
      valueProps: [
        { icon: 'ri-flow-chart', title: 'Logic Clarity', description: 'Simplified representation of complex engineering systems.' },
        { icon: 'ri-shield-flash-line', title: 'Safety Documentation', description: 'Adherence to safety codes and circuit requirements.' },
        { icon: 'ri-settings-4-line', title: 'Maintenance Asset', description: 'Essential documentation for future troubleshooting.' }
      ],
      portfolioProjects: [
        { title: 'Substation Layout', caption: 'Main electrical distribution diagram', image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&q=80&w=600' },
        { title: 'Water Treatment Plant', caption: 'Piping and instrumentation diagram (P&ID)', image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&q=80&w=600' }
      ],
      caseStudy: {
        title: 'Tech Data Center',
        description: 'Developed critical power diagrams for a Tier 4 data center, enabling rapid system commissioning and compliance verification.',
        image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80&w=600'
      }
    },
    'lighting-design--analysis': {
      title: 'Lighting Design & Analysis',
      subtitle: 'Illuminating spaces with aesthetic and scientific precision',
      heroImage: 'https://images.unsplash.com/photo-1513506003901-1e6a229e2d15?q=80&w=2000&auto=format&fit=crop',
      description: {
        what: 'We provide photometric analysis and creative lighting design, including lux level calculations, glare analysis, and fixture selection to achieve both mood and efficiency.',
        who: 'Architects looking for lighting consultation and developers needing energy code compliance reports.',
        why: 'Light defines how we experience space. Proper analysis ensures that a space is neither under-lit nor wasteful of energy, creating an optimal environment for occupants.'
      },
      valueProps: [
        { icon: 'ri-flashlight-line', title: 'Photometric Accuracy', description: 'Scientific verification of luxury and uniform distribution.' },
        { icon: 'ri-leaf-line', title: 'Energy Efficiency', description: 'Optimization for LEED and other green building standards.' },
        { icon: 'ri-eye-line', title: 'Visual Comfort', description: 'Prevention of glare and eye strain through proper planning.' }
      ],
      portfolioProjects: [
        { title: 'Art Gallery', caption: 'Precise track lighting design for exhibitions', image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=600' },
        { title: 'Parking Garage', caption: 'Safety-focused outdoor lighting analysis', image: 'https://images.unsplash.com/photo-1506521781263-d8422e82f27a?auto=format&fit=crop&q=80&w=600' }
      ],
      caseStudy: {
        title: 'Metropolis Mall',
        description: 'Redesigned the central atrium lighting, reducing energy use by 30% while increasing visitor footfall through enhanced ambiance.',
        image: 'https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&q=80&w=600'
      }
    },
    'wifi-coverage': {
      title: 'Wifi Coverage',
      subtitle: 'Advanced signal mapping for seamless connectivity',
      heroImage: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2000&auto=format&fit=crop',
      description: {
        what: 'We perform heat map analysis and wireless network design to ensure 100% signal coverage throughout complex buildings, accounting for wall materials and interference.',
        who: 'Offices, hotels, and large residential complexes where connectivity is mission-critical.',
        why: 'Unstable Wi-Fi is a major pain point in modern developments. Our coverage plans ensure that access points are placed optimally, reducing hardware costs and maximizing performance.'
      },
      valueProps: [
        { icon: 'ri-wifi-line', title: 'Dead-Zone Elimination', description: 'Identifying and fixing signal gaps before installation.' },
        { icon: 'ri-stock-line', title: 'Capacity Planning', description: 'Ensuring network speed even with hundreds of devices.' },
        { icon: 'ri-shield-keyhole-line', title: 'Asset Security', description: 'Mapping physical locations for secure network management.' }
      ],
      portfolioProjects: [
        { title: 'Campus Wi-Fi', caption: 'Site-wide heat map for university campus', image: 'https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&q=80&w=600' },
        { title: 'Logistics Warehouse', caption: 'High-reliability coverage for scanner devices', image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=600' }
      ],
      caseStudy: {
        title: 'Grand Hyatt Connectivity',
        description: 'Optimized access point placement for a 400-room hotel, resolving long-standing guest complaints about disconnected meetings.',
        image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&q=80&w=600'
      }
    },
    'lighting-coverage': {
      title: 'Lighting Coverage',
      subtitle: 'Ensuring safety and visibility with comprehensive lux mapping',
      heroImage: 'https://images.unsplash.com/photo-1506521781263-d8422e82f27a?q=80&w=2000&auto=format&fit=crop',
      description: {
        what: 'We map light distribution for outdoor areas, warehouses, and industrial sites to ensure uniform visibility and safety compliance.',
        who: 'Facility managers and civil planners focused on safety and operational visibility.',
        why: 'Poor lighting coverage leads to accidents and security blind spots. Our coverage analysis ensures that every corner is lit to required standards.'
      },
      valueProps: [
        { icon: 'ri-sun-line', title: 'Uniform Distribution', description: 'Eliminating dangerous dark spots and shadows.' },
        { icon: 'ri-shield-line', title: 'Safety Compliance', description: 'Meeting OSHA and international safety lighting standards.' },
        { icon: 'ri-community-line', title: 'Public Security', description: 'Enhanced outdoor visibility for pedestrian and vehicle safety.' }
      ],
      portfolioProjects: [
        { title: 'Steel Mill', caption: 'High-intensity industrial lighting map', image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&q=80&w=600' },
        { title: 'Airport Apron', caption: 'Precision coverage for aircraft handling areas', image: 'https://images.unsplash.com/photo-1530521954074-e64f6810b32d?auto=format&fit=crop&q=80&w=600' }
      ],
      caseStudy: {
        title: 'Valley Logistics Hub',
        description: 'Redesigned site lighting coverage for a 1M sq ft distribution center, resulting in a 40% reduction in yard accidents.',
        image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=600'
      }
    },
    'proposal-building': {
      title: 'Proposal Building',
      subtitle: 'Expert material takeoff and competitive bidding support',
      heroImage: 'https://images.unsplash.com/photo-1503387762-592dea58ef21?q=80&w=2000&auto=format&fit=crop',
      description: {
        what: 'Our proposal building service provides contractors with accurate material quantity takeoffs and labor estimates, formatted specifically for competitive bid submissions.',
        who: 'Subcontractors and general contractors who need professional-grade bid documentation to win more work.',
        why: 'Winning bids requires a delicate balance of accuracy and speed. Our service gives you the precision you need to bid confidently without leaving money on the table.'
      },
      valueProps: [
        { icon: 'ri-file-list-3-line', title: 'BIM-Linked Takeoffs', description: 'Quantities extracted directly from technical models.' },
        { icon: 'ri-speed-mini-line', title: 'Fast Turnaround', description: 'Get your bid data ready in days, not weeks.' },
        { icon: 'ri-numbers-line', title: 'Competitive Edge', description: 'Precise numbers that allow for tighter, more successful bidding.' }
      ],
      portfolioProjects: [
        { title: 'City Hospital Bid', caption: 'Complex MEP takeoff for multi-million dollar bid', image: 'https://placehold.co/600x400' },
        { title: 'Residential Phase 2', caption: 'Bulk lumber and concrete estimation', image: 'https://placehold.co/600x400' }
      ],
      caseStudy: {
        title: 'Precision Mechanical Group',
        description: 'Helped a mechanical contractor win $12M in new contracts over 6 months by providing ultra-accurate takeoff data that their competitors lacked.',
        image: 'https://placehold.co/600x400'
      }
    },
    'cost-estimation': {
      title: 'Cost Estimation',
      subtitle: 'Comprehensive financial planning for construction projects',
      heroImage: 'https://images.unsplash.com/photo-1554224155-1696413565d3?q=80&w=2000&auto=format&fit=crop',
      description: {
        what: 'We provide end-to-end cost estimation covering material costs, labor hours, equipment rentals, and contingency analysis.',
        who: 'Project owners, developers, and general contractors seeking financial clarity.',
        why: 'Construction projects fail when budgets are unrealistic. Our estimation services provide a grounded financial roadmap that helps manage expectations and prevent cost overruns.'
      },
      valueProps: [
        { icon: 'ri-calculator-line', title: 'Labor Analysis', description: 'Detailed breakdown of man-hours and crew sizing.' },
        { icon: 'ri-money-dollar-circle-line', title: 'Market-Linked Pricing', description: 'Estimates based on current regional material costs.' },
        { icon: 'ri-bar-chart-box-line', title: 'Budget Control', description: 'Clear visibility into project spend categories.' }
      ],
      portfolioProjects: [
        { title: 'Bridge Reconstruction', caption: 'Public infrastructure cost project', image: 'https://placehold.co/600x400' },
        { title: 'Retail Fit-out', caption: 'Interior renovation budgeting', image: 'https://placehold.co/600x400' }
      ],
      caseStudy: {
        title: 'Lakeside Development',
        description: 'Provided the feasibility cost estimate for a 200-unit residential project, within 2% accuracy of final construction costs.',
        image: 'https://placehold.co/600x400'
      }
    },
    'system-design': {
      title: 'System Design',
      subtitle: 'Engineering value and technical optimization',
      heroImage: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?q=80&w=2000&auto=format&fit=crop',
      description: {
        what: 'Our system design service focuses on value engineering—finding the most cost-effective technical solutions for your project without compromising on quality or performance.',
        who: 'Designers and contractors seeking to optimize project performance while reducing initial costs.',
        why: 'Over-engineering is a hidden cost in many projects. We identify areas where systems can be simplified or alternative materials used to save significant budget without impacting the final result.'
      },
      valueProps: [
        { icon: 'ri-ruler-2-line', title: 'Value Engineering', description: 'Finding cost-effective alternatives for expensive systems.' },
        { icon: 'ri-focus-3-line', title: 'Technical Optimization', description: 'Streamlining system designs for easier installation.' },
        { icon: 'ri-exchange-line', title: 'Trade Coordination', description: 'Ensuring all systems work together seamlessly.' }
      ],
      portfolioProjects: [
        { title: 'HVAC Optimization', caption: 'System redesign for 30% energy savings', image: 'https://placehold.co/600x400' },
        { title: 'Electrical Load Shedding', caption: 'Smart building power system design', image: 'https://placehold.co/600x400' }
      ],
      caseStudy: {
        title: 'Eco-Living Complex',
        description: 'Through value engineering of the HVAC and plumbing systems, we saved the client $450,000 in material costs while actually improving overall energy efficiency.',
        image: 'https://placehold.co/600x400'
      }
    }
  };

  // Get current service or fallback
  const currentService = serviceData[serviceName || 'architectural-floor-plans'] || serviceData['architectural-floor-plans'];

  // SEO Update
  useEffect(() => {
    if (currentService) {
      document.title = `${currentService.title} | Designs by DMI`;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', currentService.subtitle || currentService.description.what.substring(0, 160));
    }
    
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, [currentService]);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-[#dbcfbf]/30 to-white dark:from-slate-800/50 dark:to-slate-900 transition-colors duration-300">
          <div
            className="absolute inset-0 opacity-15"
            style={{
              backgroundImage: `url('${currentService.heroImage}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#212026] dark:text-gray-100 mb-6 transition-colors duration-300">
              {currentService.title}
            </h1>
            <p className="text-xl md:text-2xl text-[#889d8f] dark:text-gray-300 max-w-4xl mx-auto transition-colors duration-300">
              {currentService.subtitle}
            </p>
          </div>
        </section>

        {/* Service Description Section */}
        <section className="py-20 bg-white dark:bg-slate-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-8 transition-colors duration-300">
                  What We Deliver
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-[#e18033] mb-3">What it is</h3>
                    <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                      {currentService.description.what}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#e18033] mb-3">Who benefits</h3>
                    <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                      {currentService.description.who}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#e18033] mb-3">Why it matters</h3>
                    <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                      {currentService.description.why}
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img
                  src={`https://placehold.co/600x400`}
                  alt="Service Description"
                  className="w-full h-96 object-cover object-top rounded-2xl shadow-xl dark:shadow-slate-900/50"
                />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#e18033] rounded-2xl flex items-center justify-center shadow-xl dark:shadow-slate-900/50">
                  <div className="text-center text-white">
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                      <i className="ri-award-line text-2xl"></i>
                    </div>
                    <div className="text-sm font-semibold">Expert</div>
                    <div className="text-sm">Service</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Portfolio Showcase Section */}
        <section className="py-20 bg-[#dbcfbf]/20 dark:bg-slate-800/50 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">
                Portfolio Showcase
              </h2>
              <p className="text-xl text-[#889d8f] dark:text-gray-300 max-w-3xl mx-auto transition-colors duration-300">
                See our expertise in action through successful project examples
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentService.portfolioProjects.map((project: any, index: number) => (
                <Card
                  key={index}
                  className="group overflow-hidden cursor-pointer bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300"
                  onClick={() => setSelectedProject(selectedProject === index ? null : index)}
                >
                  <div className="relative h-48 bg-[#dbcfbf]/10 dark:bg-slate-700/30">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2 bg-[#e18033]/80 rounded-full">
                        <i className="ri-eye-line text-lg"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#212026] dark:text-gray-100 mb-2 group-hover:text-[#e18033] transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-[#889d8f] dark:text-gray-300 text-sm transition-colors duration-300">
                      {project.caption}
                    </p>
                  </div>

                  {/* Expanded View */}
                  {selectedProject === index && (
                    <div className="fixed inset-0 bg-[#212026]/80 dark:bg-black/90 flex items-center justify-center z-50 p-4 transition-colors duration-300">
                      <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto transition-colors duration-300">
                        <div className="relative">
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-64 md:h-96 object-cover object-top"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProject(null);
                            }}
                            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-[#e18033]/60 rounded-full flex items-center justify-center cursor-pointer transition-colors duration-300"
                          >
                            <div className="w-6 h-6 flex items-center justify-center">
                              <i className="ri-close-line text-white text-xl"></i>
                            </div>
                          </button>
                        </div>
                        <div className="p-8">
                          <h3 className="text-2xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">
                            {project.title}
                          </h3>
                          <p className="text-[#889d8f] dark:text-gray-300 text-lg leading-relaxed transition-colors duration-300">
                            {project.caption}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </section>

        <ToolsSection />
        {/* Call-to-Action Section */}
        <section className="py-20 bg-[#e18033] dark:bg-[#d16f29] transition-colors duration-300">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to get started with {currentService.title.replace(' Solutions', '')}?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let our expert team deliver precision {currentService.title.replace(' Solutions', '').toLowerCase()} services tailored to your project needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">

              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-[#e18033] dark:border-gray-300 dark:hover:bg-gray-300 dark:hover:text-slate-900 transition-all duration-300">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-phone-line"></i>
                </div>
                Contact Us
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}